import { useMutation, useQuery } from '@tanstack/react-query';
import { Posts, PostStatus, QueryParams } from '@/model';
import { PostService } from '../instances/post';
import { PostsConverter } from '@/converters/posts';
import { useCallback, useMemo } from 'react';
import { GetParamsObject } from '@/model/query-params';

export const useGetPosts = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_POSTS', params],
    async () => {
      const [err, data] = await PostService.getPost(params);
      if (err) {
        throw err;
      }
      return {
        data: PostsConverter.toDomainFromPostsPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutPostsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_POSTS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await PostService.putPost(id, payload);
      if (err) {
        throw err;
      }
      return PostsConverter.toDomainFromPutPostsResponse(data);
    },
  });
};

export const useGetPostsById = () => {
  return useMutation({
    mutationKey: ['GET_POSTS_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await PostService.getPostByUUID(uuid);
      if (err) {
        throw err;
      }
      return PostsConverter.toDomainFromGetPostsByUUIDResponse(data);
    },
  });
};

export const usePostPostsMutation = () => {
  return useMutation({
    mutationKey: ['POST_POSTS'],
    mutationFn: async (payload: any) => {
      const [err, data] = await PostService.postPost(payload);
      if (err) {
        throw err;
      }
      return PostsConverter.toDomainFromPostItemResponse(data);
    },
  });
};

export const useDeletePostsMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_POSTS'],
    mutationFn: async (id: string) => {
      const [err] = await PostService.deletePost(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePostsIfEnable = (isEnable: boolean) => {
  const query = useQuery(
    ['GET_POSTS'],
    async (): Promise<Posts[]> => {
      if (!isEnable) {
        return [];
      }

      let all: Posts[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PostService.getPost(
          GetParamsObject(
            {
              status: PostStatus.PUBLISHED,
            },
            undefined,
            900,
          ),
        );
        if (err) throw err;
        all = [...all, ...PostsConverter.toDomainFromPostsPaginatedResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};
